import { createColumnHelper } from "@tanstack/react-table";
import { T } from "@tolgee/react";
import type { TwapSliceFill } from "#/hooks/use-hyperliquid-account";
import {
	formatDetailedDateTime,
	formatPrice,
	formatSignedUsdc,
	formatSize,
	formatUsdc,
	toNumber,
} from "../../lib/formatters";
import { numericString } from "./lib/sorting-fns";

const col = createColumnHelper<TwapSliceFill>();

export const TWAP_FILLS_GRID_CLASS =
	"grid min-w-[980px] grid-cols-[1.2fr_0.8fr_0.7fr_0.9fr_0.9fr_1fr_0.8fr_0.9fr] gap-4";

export const twapFillsColumns = [
	col.accessor("fill.time", {
		id: "time",
		header: () => <T keyName="trades.columns.time" />,
		cell: ({ getValue }) => (
			<span className="text-label2-tabular-medium text-trade-text-faint">
				{formatDetailedDateTime(getValue())}
			</span>
		),
	}),
	col.accessor("fill.coin", {
		id: "coin",
		header: () => <T keyName="twap.columns.coin" />,
		cell: ({ getValue }) => (
			<span className="text-label2-medium text-trade-text-strong">{getValue()}</span>
		),
	}),
	col.accessor("fill.side", {
		id: "side",
		header: () => <T keyName="trades.columns.side" />,
		cell: ({ getValue }) => {
			const isBuy = getValue() === "B";
			return (
				<span className={isBuy ? "text-trade-green" : "text-trade-red"}>
					{isBuy ? <T keyName="general.buy" /> : <T keyName="general.sell" />}
				</span>
			);
		},
	}),
	col.accessor("fill.px", {
		id: "price",
		header: () => <T keyName="trades.columns.price" />,
		sortingFn: numericString,
		cell: ({ getValue }) => (
			<span className="text-label2-tabular-medium text-trade-text-secondary">
				{formatPrice(getValue())}
			</span>
		),
	}),
	col.accessor("fill.sz", {
		id: "size",
		header: () => <T keyName="trades.columns.size" />,
		sortingFn: numericString,
		cell: ({ getValue, row }) => (
			<span className="text-label2-tabular-medium text-trade-text-secondary">
				{formatSize(getValue())} {row.original.fill.coin}
			</span>
		),
	}),
	col.accessor((row) => toNumber(row.fill.px) * toNumber(row.fill.sz), {
		id: "tradeValue",
		header: () => <T keyName="trades.columns.trade-value" />,
		sortingFn: numericString,
		cell: ({ getValue }) => (
			<span className="text-label2-tabular-medium text-trade-text-secondary">
				{formatUsdc(getValue() as number)}
			</span>
		),
	}),
	col.accessor("fill.fee", {
		id: "fee",
		header: () => <T keyName="trades.columns.fee" />,
		sortingFn: numericString,
		cell: ({ getValue, row }) => {
			const fee = Number(getValue());
			const feeToken = row.original.fill.feeToken || "USDC";
			return (
				<span className="text-label2-tabular-medium text-trade-text-secondary">
					{fee < 0
						? formatSignedUsdc(fee, feeToken)
						: formatUsdc(fee, feeToken)}
				</span>
			);
		},
	}),
	col.accessor("fill.closedPnl", {
		id: "closedPnl",
		header: () => <T keyName="trades.columns.closed-pnl" />,
		sortingFn: numericString,
		cell: ({ getValue }) => {
			const closedPnl = Number(getValue());
			return (
				<span
					className={`text-label2-tabular-medium ${
						closedPnl === 0
							? "text-trade-text-secondary"
							: closedPnl > 0
								? "text-trade-green"
								: "text-trade-red"
					}`}
				>
					{closedPnl === 0 ? "-" : formatSignedUsdc(closedPnl)}
				</span>
			);
		},
	}),
];
